import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import ValidationMessage from "src/helpers/ValidationMessage";
import Loading from "src/helpers/Loading";

interface Permission {
  permission_key: string;
  description: string;
}
interface GroupedPermissions {
  [key: string]: Permission[];
}

const EditRole = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleId } = useParams();
  const [permissionList, setPermissionList] = useState<Permission[]>([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [validations, setValidations] = useState<Record<string, string>>({});
  const [verifiedPermission, setVerifiedPermission] = useState<string[]>([]);
  const [role, setRole] = useState("");
  const [validationPermission, setValidationPermission] = useState<any>({});
  const [irrelevantPermission, setIrrelevantPermission] = useState<any>([]);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);

  const getListOfPermissions = async (): Promise<void> => {
    let request: any = API_PATHS.permissionList;

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setPermissionList(response.permissions);
      showRole();
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

  useEffect(() => {
    getListOfPermissions();
  }, []);

  // Function to group permissions by prefix
  const groupPermissionsByPrefix = (
    permissions: Permission[]
  ): GroupedPermissions => {
    return permissions.reduce(
      (acc: GroupedPermissions, permission: Permission) => {
        const prefix = permission.permission_key.split("-")[0];

        if (!acc[prefix]) {
          acc[prefix] = [];
        }

        acc[prefix].push(permission);

        return acc;
      },
      {}
    );
  };


  const groupedPermissions = groupPermissionsByPrefix(permissionList);
 
useEffect(() => {
  const openGroups = Object.keys(groupedPermissions).filter((group) =>
    groupedPermissions[group].some(
      (permission) =>
        irrelevantPermission.includes(permission.permission_key) ||
        Object.keys(validationPermission).some((requiredKey) =>
          validationPermission[requiredKey].includes(
            permission.permission_key
          )
        )
    )
  );
  setOpenAccordions(openGroups);
}, [irrelevantPermission, validationPermission, permissionList]);

  const showRole = async () => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.showRole(Number(roleId)),
        "GET",
        API_HEADERS.authenticated
      );
      setRole(response.role.name);
      setVerifiedPermission(response.role.permissions.map((perm: any) => perm));
    } catch (error: any) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

  const handleCheckboxChange = (permissionKey: string, isChecked: boolean) => {
    if (isChecked) {
      setVerifiedPermission((prev) => [...prev, permissionKey]);
    } else {
      setVerifiedPermission((prev) =>
        prev.filter((key) => key !== permissionKey)
      );
    }
  };

  const updateRoles = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBtnLoading(true);

    const formData = {
      name: role,
      permissions: verifiedPermission,
    };
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.updateRole(Number(roleId)),
        "PATCH",
        API_HEADERS.authenticated,
        formData
      );
      setBtnLoading(false);
      FLASH_MESSAGES.successMsg(response.success);
      navigate(-1);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
     
        // Handle valid permissions
        if (error.response.data.required_permission) {
          setValidationPermission(error.response.data.required_permission);
        } else {
          setValidationPermission({});
        }

        // Handle irrelevant permissions
        if (Array.isArray(error.response.data.irrelevant_permissions)) {
          setIrrelevantPermission(error.response.data.irrelevant_permissions);
        } else {
          setIrrelevantPermission([]);
        }

        FLASH_MESSAGES.errorMsg(error.response.data.error);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={updateRoles}>
        <p className="popup_title mb-4">{t("edit_staff_subtitle")}</p>
        <div className="form-block col-12 col-sm-6 col-md-2 mb-5">
          <label className="required">{t("label_role")}</label>
          <input
            type="text"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            name="role"
            className={"form-input" + (validations.name ? " input-error" : "")}
            placeholder={t("label_role")}
          />
          {validations.name && (
            <ValidationMessage message={validations.name[0]} />
          )}
        </div>

        {loading ? (
          <div className="permission-loading mt-3">
            <Loading />
          </div>
        ) : (
          <>
            <div className="accordion custom-accordion">
              {Object.keys(groupedPermissions).map((group, index) => (
                <div className="accordion-custom-item" key={group}>
                  <button
                    className={`accordion-custom-button ${
                      openAccordions.includes(group) ? "" : "collapsed"
                    }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={"#" + group}
                    aria-expanded={openAccordions.includes(group)}
                    aria-controls={group}
                  >
                    <h4>{t("permission_group_" + group)}</h4>
                    <i className="icon arrow-down-icon" />
                  </button>

                  <div
                    id={group}
                    className={`accordion-collapse collapse ${
                      openAccordions.includes(group) ? "show" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      {groupedPermissions[group].map(
                        (permission: Permission) => (
                          <div
                            className="permission-item"
                            key={permission.permission_key}
                          >
                            <div className="permission">
                              <input
                                type="checkbox"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  boxShadow:
                                    Object.keys(validationPermission).some(
                                      (requiredKey) =>
                                        validationPermission[
                                          requiredKey
                                        ].includes(permission.permission_key)
                                    ) &&
                                    !verifiedPermission.includes(
                                      permission.permission_key
                                    )
                                      ? "0 0 5px 2px rgba(255, 0, 0, 0.3)"
                                      : "",
                                }}
                                className="form-check-input me-4"
                                id={`${permission.permission_key}`}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    permission.permission_key,
                                    e.target.checked
                                  )
                                }
                                checked={verifiedPermission.includes(
                                  permission.permission_key
                                )}
                              />
                              <label htmlFor={permission.permission_key}>
                                {permission.description}
                              </label>
                            </div>

                            {Object.keys(validationPermission).some(
                              (requiredKey) =>
                                validationPermission[requiredKey].includes(
                                  permission.permission_key
                                )
                            ) && (
                              <ValidationMessage
                                message={t("required-mesage-permission")}
                              />
                            )}

                            {irrelevantPermission.includes(
                              permission.permission_key
                            ) && (
                              <ValidationMessage
                                message={t("irrelateble_permission_message")}
                              />
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-end gap-4 mt-5">
              <button
                className="button-white"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("meal_edit_assortment_cancel_button")}
              </button>
              {btnLoading ? (
                <button className="button-blue has-icon" type="button">
                  <i className="icon spinner-icon" />
                  {t("edit_staff_saving")}
                </button>
              ) : (
                <button className="button-blue has-icon" type="submit">
                  <i className="icon edit-icon" />
                  {t("edit_staff_save")}
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default EditRole;
